import { useSyncExternalStore } from "react";
import type { Destination, TaskStatus, TaskWithFlags } from "@desk/contracts";

export function createStore<S extends object>(initial: S) {
  let state = initial;
  const listeners = new Set<() => void>();
  const store = {
    get: () => state,
    set(patch: Partial<S> | ((s: S) => Partial<S>)) {
      const p = typeof patch === "function" ? patch(state) : patch;
      state = { ...state, ...p };
      listeners.forEach((l) => l());
    },
    subscribe(l: () => void) {
      listeners.add(l);
      return () => listeners.delete(l);
    },
  };
  return store;
}

export function useStore<S extends object, T>(store: ReturnType<typeof createStore<S>>, select: (s: S) => T): T {
  return useSyncExternalStore(
    store.subscribe,
    () => select(store.get()),
    () => select(store.get()),
  );
}

export type QuickMenuKind = "status" | "priority" | "snooze" | "move";

interface UiState {
  /** Task whose detail is open (server id or client id). */
  openTaskId: string | null;
  /** "list": the open task is shown by the list it lives in (inline/panel). "overlay": opened from
   * search/palette/deep link/board, so it is presented as a sheet regardless of concept. */
  detailVia: "list" | "overlay";
  /** Keyboard cursor in the current list. */
  selectedTaskId: string | null;
  /** Multi-selection (⌘-click, ⇧-click, ⇧↓/↑). Empty or one id means single selection. */
  selectedIds: string[];
  /** Where a ⇧-click or ⇧-arrow range starts. */
  selectionAnchor: string | null;
  /** A drag is in progress; global shortcuts stand aside so arrows and Space reach the drag. */
  dragging: boolean;
  /** `status` (optional) creates the task in that status, e.g. from a status group's "+". */
  quickAdd: { open: boolean; preset?: Destination & { planToday?: boolean; status?: TaskStatus } };
  paletteOpen: boolean;
  shortcutsOpen: boolean;
  /** Keyboard-opened quick menu for the selected task (s / p / h). */
  quickMenu: { kind: QuickMenuKind; task: TaskWithFlags } | null;
  /** Destination of the screen being viewed; quick add preselects it. */
  currentDestination: (Destination & { label: string }) | null;
}

export const ui = createStore<UiState>({
  openTaskId: null,
  detailVia: "list",
  selectedTaskId: null,
  selectedIds: [],
  selectionAnchor: null,
  dragging: false,
  quickAdd: { open: false },
  paletteOpen: false,
  shortcutsOpen: false,
  quickMenu: null,
  currentDestination: null,
});

export const useUi = <T,>(select: (s: UiState) => T) => useStore(ui, select);

export function openTask(id: string | null, via: "list" | "overlay" = "list") {
  ui.set({ openTaskId: id, detailVia: via, selectedTaskId: id ?? ui.get().selectedTaskId });
}
export function openQuickAdd(preset?: UiState["quickAdd"]["preset"]) {
  ui.set({ quickAdd: { open: true, preset } });
}

/**
 * Open a task that was just created: in its list when that list shows it, otherwise as a sheet.
 * The list re-renders after the create settles, so check on the next frames.
 */
export function openCreated(id: string, isListed: (id: string) => boolean) {
  const hash = window.location.hash;
  openTask(id, "list");
  requestAnimationFrame(() => requestAnimationFrame(() => {
    if (window.location.hash === hash && ui.get().openTaskId === id && ui.get().detailVia === "list" && !isListed(id)) openTask(id, "overlay");
  }));
}
